'use client';
import Loading from '@/app/(withLayout)/loading';
import { OnbForm } from '@/components/form';
import {
  InstallmentCard,
  InstallmentHeader,
  InstallmentSummary,
  installmentNotes,
} from '@/components/installmentSummary';
import { sendOnbEvent } from '@/components/newRelic';
import { useStepData } from '@/components/stepData';
import { connectedField } from '@/redux/fields';
import { pushStepVariables } from '@/redux/flowSlice';
import { showModal } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Button, Collapse, Grid, Typography } from '@mui/material';
import { customerRegistration, installment, repaymentTerms } from '@repo/onb-api';
import { COLORS, OrderCard, PlanOption, RadioGroup } from '@repo/ui';
import { deleteClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function RepaymentTermsPayments({
  stepname: stepName,
  stepdata: stepDataProp,
}: {
  stepname: repaymentTerms['name'];
  stepdata?: repaymentTerms['variables'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const router = useRouter();
  const [paymentEdit, setPaymentEdit] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<installment>();
  const dispatch = useDispatch();
  const additionalData = useSelector((state: RootState) => state.additionalData);
  const { merchantName = '', price = '', merchantOrderId = '', policyProduct } = additionalData;
  const [sortedInstallments, setSortedInstallments] = useState<installment[]>();
  if (merchantOrderId === '') {
    deleteClientCookie(['instanceId', 'utmData']);
    router.push('/');
  }

  useEffect(() => {
    setSortedInstallments([...(stepData?.installments || [])].sort((a, b) => a.count - b.count));
  }, [stepData?.installments]);

  useEffect(() => {
    if (!stepData?.installmentPlanId) {
      dispatch(
        pushStepVariables({
          stepName,
          variable: 'installmentPlanId',
          value: sortedInstallments?.[sortedInstallments?.length - 1]?.planId,
        }),
      );
    }
    if (!stepData?.requestedAmount) {
      dispatch(
        pushStepVariables({
          stepName,
          variable: 'requestedAmount',
          value: parseFloat(price) || 0,
        }),
      );
    }
  }, [sortedInstallments, stepData?.installmentPlanId, dispatch]);
  useEffect(() => {
    if (selectedPlan?.planId !== stepData?.installmentPlanId) {
      setSelectedPlan(sortedInstallments?.find(({ planId }) => planId === stepData?.installmentPlanId));
    }
  }, [sortedInstallments, stepData?.installmentPlanId]);
  useEffect(() => {
    if (sortedInstallments?.length === 0) {
      sendOnbEvent({ type: 'error', subtype: 'noInstallments', data: { stepName }, severity: 'high' });
      router.push('/error');
    }
  }, [sortedInstallments, stepName, router]);

  return (stepData?.installments?.length || 0) === 0 ? (
    <Loading />
  ) : (
    <StepWrapper>
      <Typography variant="h1">¡Buenas noticias!</Typography>
      <Typography component="p" variant="subtitle2">
        Pagarás de la siguiente forma:
      </Typography>

      <OnbForm<customerRegistration> stepName={stepName!}>
        <OrderCard
          merchantName={merchantName}
          merchantOrderId={merchantOrderId}
          price={price}
          isLoan={policyProduct === 'EFECTIVOC'}
        />
        <InstallmentCard>
          <Collapse in={!paymentEdit}>
            <InstallmentSummary
              label={policyProduct === 'EFECTIVOC' ? 'Lo pagás en' : 'Tu compra en cuotas'}
              count={selectedPlan?.count}
              amount={selectedPlan?.amount}
              withInterest={selectedPlan?.withInterest}
              onEdit={(sortedInstallments?.length || 0) > 1 ? () => setPaymentEdit(true) : undefined}
              notes={installmentNotes(selectedPlan?.planType, selectedPlan?.firstDueDate)}
            />
          </Collapse>
          <Collapse in={paymentEdit}>
            <Grid>
              <InstallmentHeader
                label="Seleccioná un plan"
                action={{ icon: 'arrow_back', title: 'Volver', onClick: () => setPaymentEdit(false) }}
              />
              <RadioGroup
                aria-label="Planes de cuotas"
                // Same cards as the efectivo plan picker: roomy rows, white even when picked
                sx={{
                  '& >label:nth-last-of-type(1)': { margin: '0px' },
                  '& .MuiFormControlLabel-root': { py: 1.5 },
                  '& .MuiFormControlLabel-label': { flex: 1 },
                  '& .MuiFormControlLabel-root:has(.Mui-checked)': { borderColor: COLORS.brand.main },
                }}
                options={sortedInstallments?.map(({ amount, count, planId, withInterest, finalAmount }) => ({
                  label: (
                    <PlanOption count={count} amount={amount} finalAmount={finalAmount} withInterest={withInterest} />
                  ),
                  value: planId,
                }))}
                {...connectedField({
                  stepName,
                  name: 'installmentPlanId',
                  required: true,
                  beforeChange: (value) => parseInt(value),
                  // Picking a plan is the confirmation: fold the picker back into the summary
                  onChange: () => setPaymentEdit(false),
                })}
              />
            </Grid>
          </Collapse>
        </InstallmentCard>
        <Button variant="text" onClick={() => dispatch(showModal({ body: 'selectedPlan', title: 'Plan de pagos' }))}>
          Ver plan de pagos
        </Button>
      </OnbForm>
    </StepWrapper>
  );
}
